import React, { useState } from "react";
import api from "@/lib/api";
import { rm } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Sun, Sparkles, TrendingUp } from "lucide-react";

export default function CeoBrief() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/ai/ceo-brief"); setBrief(r.data); }
    catch (e) { toast.error("Failed to generate brief"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-float-up" data-testid="brief-panel">
      {!brief && !loading && (
        <div className="bg-card border border-border rounded-2xl p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-4"><Sun className="w-7 h-7 text-primary" /></div>
          <h3 className="font-display text-xl font-bold mb-2">Daily CEO Brief</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">An AI-written morning briefing summarising yesterday's spend, leads, sales, revenue and prioritised actions.</p>
          <Button data-testid="brief-generate" onClick={load} className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl px-8"><Sparkles className="w-4 h-4 mr-1" /> Generate Brief</Button>
        </div>
      )}

      {loading && <div className="flex flex-col items-center py-16 gap-3"><Loader2 className="w-7 h-7 animate-spin text-primary" /><p className="text-sm text-muted-foreground">Writing your brief…</p></div>}

      {brief && (
        <>
          <div className="bg-card border border-border rounded-2xl p-6">
            <h3 className="font-display text-xl font-bold mb-1">{brief.greeting || "Good Morning"}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
              {[
                { l: "Spend", v: rm(brief.summary?.spend) },
                { l: "Qualified Leads", v: brief.summary?.qualified_leads },
                { l: "Sales", v: brief.summary?.sales },
                { l: "Revenue", v: rm(brief.summary?.revenue) },
                { l: "ROAS", v: (brief.summary?.roas || 0) + "x" },
              ].map((x) => (
                <div key={x.l} className="bg-secondary/30 rounded-xl p-3 text-center"><p className="font-display text-lg font-bold text-primary">{x.v}</p><p className="text-[10px] text-muted-foreground mt-0.5">{x.l}</p></div>
              ))}
            </div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-accent mb-3 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> AI Priorities</p>
            <ul className="space-y-2">{(brief.priorities || []).map((p, i) => (
              <li key={i} className="flex gap-3 text-sm"><span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>{p}</li>
            ))}</ul>
            {brief.closing && <p className="text-sm text-muted-foreground mt-4 italic">{brief.closing}</p>}
          </div>
          <Button variant="outline" data-testid="brief-regenerate" onClick={load} className="rounded-xl">Regenerate</Button>
        </>
      )}
    </div>
  );
}
